import { isSpeechRecognitionSupported } from "@jugl-web/utils/speechRecognition";
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import {
  VoiceModeSession,
  type VoiceModeSessionOptions,
  type VoiceModeSessionSnapshot,
} from "../../services/VoiceModeSession";
import enterCueUrl from "./assets/voice-mode-enter.mp3";
import leaveCueUrl from "./assets/voice-mode-leave.mp3";

interface UseVoiceModeSessionOptions extends VoiceModeSessionOptions {
  /** When true, skips VoiceModeSession creation and start/stop lifecycle. */
  skip?: boolean;
}

const EMPTY_SNAPSHOT: VoiceModeSessionSnapshot = {
  transportStatus: "idle",
  playbackStatus: "idle",
  micStatus: "idle",
  isMuted: false,
  isUserSpeaking: false,
  isDiscardingAgentStream: false,
  waveformSamples: null,
};

const emptySubscribe = (): (() => void) => () => {};

const getEmptySnapshot = (): VoiceModeSessionSnapshot => EMPTY_SNAPSHOT;

/**
 * React wrapper around VoiceModeSession.
 * Creates and runs a session while `skip` is false; returns null session otherwise.
 */
export const useVoiceModeSession = ({
  skip = false,
  ...options
}: UseVoiceModeSessionOptions) => {
  const [session, setSession] = useState<VoiceModeSession | null>(null);

  const optionsRef = useRef(options);
  optionsRef.current = options;

  const hasPlayedEnterCueRef = useRef(false);

  // Create/start before paint when entering voice mode; dispose when skipping.
  useLayoutEffect(() => {
    if (skip) {
      return undefined;
    }

    if (!isSpeechRecognitionSupported()) {
      optionsRef.current.onFatalError?.("speech-recognition-unsupported");
      return undefined;
    }

    const nextSession = new VoiceModeSession(optionsRef.current);
    nextSession.start();
    setSession(nextSession);

    return () => {
      nextSession.dispose();
      setSession((current) => (current === nextSession ? null : current));
    };
  }, [skip]);

  const snapshot = useSyncExternalStore(
    session ? session.subscribe : emptySubscribe,
    session ? session.getSnapshot : getEmptySnapshot
  );

  // Play the enter cue when the transport connects after entering voice mode
  useEffect(() => {
    if (skip) {
      return;
    }

    if (
      snapshot.transportStatus === "connected" &&
      !hasPlayedEnterCueRef.current
    ) {
      safePlayAudio(enterCueUrl);
      hasPlayedEnterCueRef.current = true;
    }
  }, [skip, snapshot.transportStatus]);

  // Play the leave cue when explicitly exiting voice mode or when the hook unmounts
  useEffect(() => {
    if (skip) {
      return undefined;
    }

    return () => {
      safePlayAudio(leaveCueUrl);
      hasPlayedEnterCueRef.current = false;
    };
  }, [skip]);

  return { session, snapshot };
};

const safePlayAudio = (src: string): void => {
  const audio = new Audio(src);
  audio.play().catch(() => {
    // Not critical, skip the error
  });
};
